import { DataGrid, Popup } from "devextreme-react";
import Button from "devextreme-react/button";
import { Column, FilterRow, Item, Toolbar } from "devextreme-react/data-grid";
import { ButtonItem, Form, Item as FormItem } from "devextreme-react/form";
import { LookupTypes } from "devextreme-react/lookup";
import notify from "../../lib/notify";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { subordinateEmployee } from "../../api/auth";
import {
  addSubstitution,
  deleteSubstitution,
  downloadSubstitution,
} from "../../api/substitution";

type SubstitutionPopupProps = {
  managerId: number;
  managerCompanyId: number;
  onClose: (accountUnlocked: boolean) => void;
};

export default function SubstitutionPopup({
  managerId,
  managerCompanyId,
  onClose,
}: SubstitutionPopupProps) {
  const [popupSubstitutionVisible, setPopupSubstitutionVisible] =
    useState(false);
  const { t } = useTranslation();
  const [substitution, setSubsstitution] = useState<
    {
      substitutionId: number;
      managerFirstName: string;
      managerLastName: string;
      substitutionManagerFirstName: string;
      substitutionManagerLastName: string;
      from: Date;
      to: Date;
    }[]
  >([]);
  const [employeesManager, setEmployeesManager] = useState<
    {
      employeeId: number;
      firstName: string;
      lastName: string;
      companyId: number;
    }[]
  >([]);
  const [substitutionManagerId, setSubstitutionManagerId] = useState<number>();
  const [substitutionManagerCompanyId, setSubstitutionManagerCompanyId] =
    useState<number>();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(
    new Date(new Date().setDate(new Date().getDate() + 7))
  );

  const fetchSubstitution = useCallback(async () => {
    const result = await downloadSubstitution(managerId, managerCompanyId);
    if (result.isOk) {
      setSubsstitution(result.data);
    }
  }, [managerCompanyId, managerId]);

  const createSubstitution = async () => {
    var result = await addSubstitution(
      managerId,
      managerCompanyId,
      substitutionManagerId || 0,
      substitutionManagerCompanyId || 0,
      selectedDate,
      selectedEndDate
    );
    if (result.isOk) {
      notify(t("substitution.createdMessage"), "success", 5000);
      fetchSubstitution();
    } else {
      notify(
        `${t("Error:")} ${result.error || t("Unknown error")}`,
        "error",
        5000
      );
    }
  };

  const downloadSubordinateEmployees = async () => {
    const response = await subordinateEmployee(managerCompanyId, managerId);
    if (response.isOk) {
      setEmployeesManager(response.data);
    } else {
      const rolesList = response.data.data || response.data || [];
      setEmployeesManager(rolesList);
    }
  };

  const deleteSubstitutionManager = async (id: number) => {
    var result = await deleteSubstitution(id);
    if (result.isOk) {
      fetchSubstitution();
      notify(t("substitution.deleteMessage"), "success", 5000);
    } else {
      notify(
        `${t("Error:")} ${result.error || t("Unknown error")}`,
        "error",
        5000
      );
    }
  };

  const deleteSubordinateEmployeeHandler = (sub: any) => {
    deleteSubstitutionManager(sub.substitutionId);
  };

  const addPopupUser = () => {
    downloadSubordinateEmployees().then(() =>
      setPopupSubstitutionVisible(true)
    );
  };

  const closeSubstitutionCreatePopup = useCallback(() => {
    setPopupSubstitutionVisible(false);
    setSubstitutionManagerId(undefined);
    setSelectedDate(new Date());
    setSelectedEndDate(new Date(new Date().setDate(new Date().getDate() + 7)));
  }, []);

  useEffect(() => {
    fetchSubstitution();
  }, [fetchSubstitution]);

  return (
    <>
      <Popup
        visible
        onHiding={() => onClose(false)}
        maxWidth={1500}
        height="auto"
        maxHeight="80vh"
        title={t("substitution.substitution")}
        showCloseButton
        dragEnabled
      >
        <div className="popup-projects">
          <div className="data-grid-projects">
            <DataGrid
              dataSource={substitution}
              keyExpr="substitutionId"
              showBorders
              remoteOperations={false}
              allowColumnResizing
              allowColumnReordering
              searchPanel={{
                visible: true,
                placeholder: t("searchPlaceholder"),
              }}
              paging={{
                pageSize: 10,
              }}
              pager={{
                visible: true,
                showInfo: true,
                showNavigationButtons: true,
              }}
            >
              <Column
                dataField="managerLastName"
                dataType="string"
                caption={t("substitution.managerLastName")}
                minWidth={200}
              />
              <Column
                dataField="managerFirstName"
                dataType="string"
                caption={t("substitution.managerFirstName")}
                minWidth={200}
              />
              <Column
                dataField="from"
                dataType="date"
                caption={t("substitution.from")}
                minWidth={100}
              />
              <Column
                dataField="to"
                dataType="date"
                caption={t("substitution.to")}
                minWidth={100}
              />
              <Column
                dataField="substitutionManagerLastName"
                dataType="string"
                caption={t("substitution.substitutionManagerLastName")}
                minWidth={275}
              />
              <Column
                dataField="substitutionManagerFirstName"
                dataType="string"
                caption={t("substitution.substitutionManagerFirstName")}
                minWidth={250}
              />
              <Column
                alignment="right"
                type="buttons"
                name="deleteUser"
                buttons={[
                  {
                    icon: "trash",
                    onClick: (e) => {
                      if (e.row && !e.row.data.isRegistered) {
                        deleteSubordinateEmployeeHandler(e.row.data);
                      }
                    },
                    disabled: (e) => (e.row ? e.row.data.isRegistered : true),
                    hint: t("substitution.removeSubstitution"),
                  },
                ]}
              />
              <Toolbar>
                <Item
                  name="searchPanel"
                  location="before"
                  locateInMenu="auto"
                  cssClass="datagrid-search-panel-without-margin-left"
                />
                <Item location="after" locateInMenu="auto">
                  <Button
                    type="default"
                    onClick={addPopupUser}
                    text={t("substitution.addSubstitution")}
                    icon="add"
                  />
                </Item>
              </Toolbar>
              <FilterRow visible />
            </DataGrid>
          </div>
        </div>
      </Popup>
      {popupSubstitutionVisible ? (
        <Popup
          visible
          onHiding={closeSubstitutionCreatePopup}
          maxWidth={400}
          height="auto"
          maxHeight="80vh"
          title={t("substitution.createSubstitution")}
          showCloseButton
          dragEnabled
        >
          <form className="substitution-form">
            <Form colCount={2}>
              <FormItem
                dataField={t("substitution.employee")}
                editorType="dxLookup"
                editorOptions={
                  {
                    showClearButton: true,
                    items: employeesManager,
                    displayExpr: (user) =>
                      user ? `${user.lastName} ${user.firstName}` : "",
                    valueExpr: "employeeId",
                    value: substitutionManagerId,
                    onValueChanged: (e) => {
                      setSubstitutionManagerId(e.value);
                      const selectedManager = employeesManager.find(
                        (employeesManager) =>
                          employeesManager.employeeId === e.value
                      );
                      if (selectedManager) {
                        setSubstitutionManagerCompanyId(
                          selectedManager.companyId
                        );
                      }
                    },
                    dropDownOptions: {
                      showTitle: false,
                    },
                  } as LookupTypes.Properties
                }
                colSpan={2}
              />
              <FormItem
                dataField={t("holidayPopupChoose.startDate")}
                editorType="dxDateBox"
                editorOptions={{
                  value: selectedDate,
                  type: "date",
                  onValueChanged: (e: any) => setSelectedDate(e.value),
                }}
              />
              <FormItem
                dataField={t("holidayPopupChoose.endDate")}
                editorType="dxDateBox"
                editorOptions={{
                  value: selectedEndDate,
                  type: "date",
                  onValueChanged: (e: any) => setSelectedEndDate(e.value),
                }}
              />
              <ButtonItem
                buttonOptions={{
                  text: t("substitution.send"),
                  type: "default",
                  onClick: () => {
                    createSubstitution();
                    closeSubstitutionCreatePopup();
                  },
                }}
                colSpan={2}
              />
            </Form>
          </form>
        </Popup>
      ) : null}
    </>
  );
}
